import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { format, parseISO } from "date-fns";
import useGetSales, {
    TTipReportV2Results,
    EModules,
    TTipReportOrderKeys,
    getSalesAsync,
} from "api/useGetSales";
import { PaginationState } from "components/Table";
import { ESortOrder, ISortState } from "components/Table/types";
import {
    IHeadObjectXLS,
    TActionProps,
    TGeneratePdfFunction,
    TGenerateXLSFunction,
} from "components/Table/components/ActionButtons/types";
import apiDateFormat from "constants/apiDateFormat";
import { useDebounce } from "hooks";
// import { EAPIProviderRanks, TProviders } from "types/APIProviderRanks";
import { IProviderCheckList } from "types/IProviderCheckList";
import { filterColumnsV2 } from "utils/filterColumns";
import {
    ELocalSavedFilters,
    ISavedDefaultFilters,
    getDefaultFilters,
    setDefaultFiltersV2,
    tipReportDefaultProviders,
} from "utils/defaultFilters";
import formatOrderingAPI from "utils/formatOrderingAPI";
import { parseAsArrayOf, parseAsInteger, parseAsString, useQueryStates } from 'nuqs';
import { inventories, TInventoryOptionValue } from "constants/common";
import { onGeneratePDFDefaultType, reportNaming } from "utils/helper";
import { useAppDispatch, useAppSelector } from "hooks/store";
import { selectUserData, setDealerV2, setInventory } from "store/auth/authSlice";
import { selectDealershipsData } from "store/dealership/dealershipSlice";
import { Box, Button, Icon, IconButton, Switch, Text } from "@chakra-ui/react";
import DataTable from "components/v2/DataTable";
import { ActionButtonV2 } from "components/Table/components/ActionButtons";
import { CustomizeProvider } from "components/v2/CustomizeProvider";
import { ColumnSizingState, SortingState, VisibilityState } from "@tanstack/react-table";
import { getColumns, getKockOutXLSColumns, getPdfColumns, getXLSColumns } from "./constants";
import { threeDaysToToday } from "constants/commonDateRanges";
import RangePopover from "components/v2/Popover/RangePopover";
import { FaMoneyBillTrendUp } from "react-icons/fa6";
import usePostUsers, { EUserModules } from "api/usePostUsers";
import { HiOutlineMinusCircle, HiOutlinePlusCircle } from "react-icons/hi";

type TSavedFilters = ISavedDefaultFilters[ELocalSavedFilters.TipReportTableV2];

const TipReportV2 = () => {

    const dispatch = useAppDispatch();
    const userAuth = useAppSelector(selectUserData);
    const { dealerships } = useAppSelector(selectDealershipsData);

    const { mutate: savePagePreference } = usePostUsers<EUserModules.PagePreference>({
        module: EUserModules.PagePreference,
    });

    const [defaultFilters] = useState(() => getDefaultFilters<TSavedFilters>(
        ELocalSavedFilters.TipReportTableV2,
        tipReportDefaultProviders
    ));

    const initPriceChange = defaultFilters.price_changes.find(
        (pc) => pc.dealership_uid === userAuth.dealerV2.uid
    );

    const [query, setQuery] = useQueryStates({
        startDate: {
            parse: (value) => value,
            serialize: (value) => value ?? '',
            defaultValue: format(threeDaysToToday.startDate, apiDateFormat),
        },
        endDate: {
            parse: (value) => value,
            serialize: (value) => value ?? '',
            defaultValue: format(threeDaysToToday.endDate, apiDateFormat),
        },
        source: parseAsArrayOf(parseAsString).withDefault([]),
        sortBy: {
            parse: (value) => value,
            serialize: (value) => value ?? '',
            defaultValue: '',
        },
        search: {
            parse: (value) => value,
            serialize: (value) => value ?? '',
            defaultValue: '',
        },
        dol_from: {
            parse: (value) => value,
            serialize: (value) => value ?? '',
            defaultValue: '',
        },
        dol_to: {
            parse: (value) => value,
            serialize: (value) => value ?? '',
            defaultValue: '',
        },
        min_price_change: {
            parse: (value) => value,
            serialize: (value) => value ?? '',
            defaultValue: '',
        },
        max_price_change: {
            parse: (value) => value,
            serialize: (value) => value ?? '',
            defaultValue: '',
        },
        max_range: {
            parse: (value) => value,
            serialize: (value) => value ?? '',
            defaultValue: '',
        },
        page: parseAsInteger.withDefault(0),
        page_size: parseAsInteger.withDefault(defaultFilters.pageSize),
        link_from: {
            parse: (value) => value,
            serialize: (value) => value ?? '',
            defaultValue: '',
        },
        inventory: {
            parse: (value) => value as TInventoryOptionValue,
            serialize: (value) => value,
            defaultValue: undefined,
        },
        dealership_uid: {
            parse: (value) => value,
            serialize: (value) => value,
            defaultValue: undefined,
        },
        dealership_name: {
            parse: (value) => value,
            serialize: (value) => value,
            defaultValue: undefined,
        },
        knockout: {
            parse: (value) => value === 'true',
            serialize: (value) => value ? 'true' : 'false',
            defaultValue: false,
        }
    });

    const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
        defaultFilters.columnVisibility && Object.keys(defaultFilters.columnVisibility).length > 0 && defaultFilters.isDefaultTipReportConfigSet ? defaultFilters.columnVisibility : { 
            vin: false,
            age: false,
            // cgr
            cgr_price: false,
            cgr_new_price: false,

            // atc
            
            atc_price: false,
            atc_new_price: false,
            atc_great_deal_rating: false,
            atc_price_at_great_deal_rating: false,
            atc_price_change_at_great_deal_rating: false,

            ccm_price: false,
            ccm_new_price: false,
            ccm_great_deal_rating: false,
            ccm_price_at_great_deal_rating: false,
            ccm_price_change_at_great_deal_rating: false,

            cfx_price: false,
            cfx_new_price: false,
            cfx_great_deal_rating: false,
            cfx_price_at_great_deal_rating: false,
            cfx_price_change_at_great_deal_rating: false,
        }
    );

    const [columnSizing, setColumnSizing] = useState<ColumnSizingState>(defaultFilters.isDefaultTipReportConfigSet ? defaultFilters.columnSizing || {} : {});

    const [sourceInteracted, setSourceInteracted] = useState(
        () => query.source.length > 0 || !!query.link_from
    );

    const providers = useMemo<IProviderCheckList[]>(() => {
        const hasUrlSource = sourceInteracted || query.source.length > 0;
        return userAuth.dealerV2.third_party_provider.map((provider) => {
            if (hasUrlSource || query.link_from) {
                return { id: provider.key, label: provider.label, value: query.source.includes(provider.key) } as IProviderCheckList;
            }
            const saved = defaultFilters.providers.find(({ id }) => id === provider.key);
            return { id: provider.key, label: provider.label, value: saved?.value ?? false } as IProviderCheckList;
        });
    }, [sourceInteracted, userAuth.dealerV2.third_party_provider, query.source, query.link_from, defaultFilters.providers]);

    const defaultPriceChange = useMemo(
        () =>
            defaultFilters.price_changes.find(
            (pc) => pc.dealership_uid === userAuth.dealerV2.uid
            ),
        [defaultFilters.price_changes, userAuth.dealerV2.uid]
    );

    const effectiveMinPrice = query.min_price_change ? parseInt(query.min_price_change) : (defaultPriceChange?.min_price_change ?? 0);
    const effectiveMaxPrice = query.max_price_change ? parseInt(query.max_price_change) : (defaultPriceChange?.max_price_change ?? 500);

    const effectiveMaxRange = query.max_range ? parseInt(query.max_range) : (initPriceChange?.max_price_change ?? 500);

    // Sync state when defaultPriceChange or params change
    // useEffect(() => {
    //     // Only update if params are present (i.e., from URL, not from user interaction)
    //     const newMin = minPriceChangeParams
    //         ? parseInt(minPriceChangeParams)
    //         : defaultPriceChange?.min_price_change || 0;
    //     const newMax = maxPriceChangeParams
    //         ? parseInt(maxPriceChangeParams)
    //         : defaultPriceChange?.max_price_change || 500;

    //     setMinPriceChange(newMin);
    //     setMaxPriceChange(newMax);
    //     setMaxRange(newMax);
        
    //     console.log("minPriceChangeParams", minPriceChangeParams);
    // }, [defaultPriceChange, minPriceChangeParams, maxPriceChangeParams]);
    
    useEffect(() => {
        
        const newMinPriceChange = defaultPriceChange?.min_price_change || 0;
        const newMaxPriceChange = defaultPriceChange?.max_price_change || 500;
        
        setQuery((prev) => ({
            ...prev,
            min_price_change: prev.min_price_change || newMinPriceChange.toString(),
            max_price_change: prev.max_price_change || newMaxPriceChange.toString(),
            max_range: prev.max_range || newMaxPriceChange.toString(),
        }));
        
    }, [defaultPriceChange]);

    useEffect(() => {
        if (query.dealership_uid && query.dealership_uid !== userAuth.dealerV2.uid) {
            const selectedDealer = dealerships.find((d) => d.uid === query.dealership_uid);
            if (selectedDealer) {
                dispatch(setDealerV2({ dealerV2: selectedDealer }));
            }
        }

        if (query.inventory && query.inventory !== userAuth.inventory.value) {
            const matchingInventory = inventories.find((inv) => inv.value === query.inventory);
            if (matchingInventory) {
                dispatch(setInventory({ inventory: matchingInventory }));
            }
        }
    }, [query.dealership_uid, query.inventory, dealerships, dispatch, userAuth.dealerV2.uid, userAuth.inventory.value]);


    // const [sortBy, setSortBy] = useState<ISortState>(
    //     sortByParams && typeof sortByParams === "string"
    //         ? {
    //             column: sortByParams.replace("-", ""),
    //             order: sortByParams.startsWith("-")
    //                 ? ESortOrder.Descending
    //                 : ESortOrder.Ascending,
    //         }
    //         : {
    //             column: null,
    //             order: ESortOrder.None,
    //         }
    // );

    const sortBy = useMemo<SortingState>(() =>
        query.sortBy ? [{
            id: query.sortBy.replace("-", ""),
            desc: query.sortBy.startsWith("-"),
        }] : [],
    [query.sortBy]);

    const [quickSearch, setQuickSearch] = useState<string>(query.search);
    const quickSearchApi = useDebounce<string>(quickSearch, 350);
    const [knockout, setKnockout] = useState<boolean>(query.knockout);



    const sortedBy: ISortState = useMemo(() => {

        if (!sortBy.length) {
            return {
                column: null,
                order: ESortOrder.None,
            };
        }
        return {
            column: sortBy[0].id as TTipReportOrderKeys,
            order: sortBy[0].desc ? ESortOrder.Descending : ESortOrder.Ascending,
        };
    }, [sortBy]);

    const fetchParameters = useMemo(
        () => ({
            module: EModules.TipReportV2,
            params: {
                date_from: query.startDate,
                date_to: query.endDate,
                dealership_uid: userAuth.dealerV2.uid,
                ...(query.dol_from && !isNaN(parseInt(query.dol_from)) && {
                dol_from: parseInt(query.dol_from)
                }),
                ...(query.dol_to && !isNaN(parseInt(query.dol_to)) && {
                dol_to: parseInt(query.dol_to)
                }),
                // dol_range: dolRangeParams as EDaysOnLot,
                // limit: pageSize,
                // offset,
                ordering: formatOrderingAPI<TTipReportOrderKeys>(sortedBy),
                search: quickSearchApi,
                source: providers.filter(({ value }) => value).map(({ id }) => id),
                min_price_change: effectiveMinPrice,
                max_price_change: effectiveMaxPrice,
                page: query.page + 1,
                page_size: query.page_size,
                knockout: knockout,
            },
        }),
        [query.startDate, query.endDate, userAuth.dealerV2.uid, query.dol_from, query.dol_to, sortedBy, quickSearchApi, providers, effectiveMinPrice, effectiveMaxPrice, query.page, query.page_size, knockout]
    );

    const { data, isFetching } = useGetSales<EModules.TipReportV2>(fetchParameters);

    // useEffect(() => {
    //     setDefaultFilters<TSavedFilters>(ELocalSavedFilters.TipReportTableV2, {
    //         dateRange,
    //         pageSize,
    //         providers,
    //         columnSizing,
    //         columnVisibility,
    //         // isDefaultTipReportConfigSet: true // to indicate that the default config has been set 
    //     });
    // }, [columnSizing, columnVisibility, dateRange, pageSize, providers]);

    const onSort = useCallback((sort: SortingState) => {
        // if (!sort.length) return;
        setQuery((prev) => ({
            ...prev,
            sortBy: sort.length > 0 ? `${sort[0].desc ? '-' : ''}${sort[0].id}` : '',
        }));
    }, [setQuery]);

    const filteredColumns = useMemo(
        () =>
            filterColumnsV2(
                getColumns(userAuth.inventory.value, providers, knockout),
                providers
            ),
        [providers, userAuth.inventory.value, knockout]
    );

    const onDonwloadKnockOut: TActionProps<TGenerateXLSFunction> = useCallback(
        async (generateXLS, setIsLoading, provider) => {
            setIsLoading(true);
            
            try {
                // let hasNextPage = true;
                let collectedResults: TTipReportV2Results[] = [];
                // let mapOffset = defaultOffset;

                let hasNextPage = true;

                let tempPage = 1;

                do {
                    const { next, results } =
                        await getSalesAsync<EModules.TipReportV2>({
                            ...fetchParameters,
                            params: {
                                ...fetchParameters.params,
                                ...(!knockout && {
                                    max_price_change: 2000,
                                }),
                                page_size: 1000,
                                page: tempPage,
                                // offset: mapOffset,
                            },
                        })();

                    collectedResults = [...collectedResults, ...results];

                    tempPage++;
                    // console.log('next', next);

                    hasNextPage = next ? true : false;
                    // mapOffset = mapOffset + defaultPageSize;
                } while (hasNextPage);

                let groupedHeadFormatted: [IHeadObjectXLS[], (string | IHeadObjectXLS)[]] = [[], []];

                // filter providers
                const filteredProviders = providers.filter(({ value }) => value);

                // filter columns
                const filteredXlsColumns = getKockOutXLSColumns(userAuth.inventory.value, columnVisibility).map((column) => {
                        if (column.columns) {
                            column.columns = column.columns.filter(
                                (groupedColumn) =>
                                    !groupedColumn.filterColumnId ||
                                    filteredProviders.some(
                                        (provider) => provider.id === groupedColumn.filterColumnId
                                    )
                            );
                        }
                        return column;
                    })

                    .filter(
                        (column) =>
                            !column.filterColumnId ||
                            filteredProviders.some(
                                (provider) => provider.id === column.filterColumnId
                            )
                    );

                
                const filteredColumnsToCheck: string[] = filteredProviders
                    // .filter((provider) => provider.value) // Only include providers that are selected
                    .flatMap((provider) => [
                      `${provider.id.toLowerCase()}_deal_rating`,
                      `${provider.id.toLowerCase()}_new_deal_rating`,
                      `${provider.id.toLowerCase()}_price_change`,
                    ]);
                  
                // Filter out rows where any of the specified columns have invalid values
                // const filteredCollectedResults = collectedResults.filter((result) => {
                //     const invalidValues = ['nan', ''];
                  
                //     // Check if any of the dynamically constructed columns have invalid values
                //     return !filteredColumnsToCheck.some((column) => {
                //         // Check if the column does not exist in the result or has an invalid value
                //         return !(column in result) || invalidValues.includes(result[column as keyof TTipReportV2Results] as string);
                //     });
                // });

                // rest of the code remains the same
                const bodyFormatted = collectedResults.map((values) => {
                    const groupedColumns = filteredXlsColumns
                        // .filter(({ filterColumnId: columnFilterId }) => columnFilterId === filterColumnId)
                        .map(({ columns: groupColumns, func, key, filterColumnId }) => {
                            if (!groupColumns) {
                                
                              const tempValue = (func?.(values[key], values) as string) || (values[key] as string)
                              
                              return tempValue === 'nan' ? '' : tempValue;
                              // return (
                              //       (func?.(values[key], values) as string) ||
                              //       (values[key] as string)
                              //   );
                            } else {
                                return groupColumns.map(({ func, key }) => {
                                    // console.log('func', values);

                                    const tempValue = (func?.(values[key], values) as string) || (values[key] as string)
                                    
                                    return tempValue === 'nan' ? '' : tempValue;
                                    // return (
                                    //     (func?.(values[key], values) as string) ||
                                    //     (values[key] as string)
                                    // );
                                });
                            }
                        });

                    return groupedColumns.flat();
                });

                for (
                    let indexHeadColumn = 0;
                    indexHeadColumn < filteredXlsColumns.length;
                    indexHeadColumn++
                ) {
                    const {
                        colspan,
                        label,
                        bgColor,
                        textColor,
                        columns: groupedColumns,
                    } = filteredXlsColumns[indexHeadColumn];

                    groupedHeadFormatted[0].push({ colspan, label, bgColor, textColor });

                    if (groupedColumns && groupedColumns.length) {
                        groupedColumns.map(({ label, bgColor: colBgColor, textColor }) =>
                            groupedHeadFormatted[1].push(
                                colBgColor || textColor
                                    ? { label, bgColor: colBgColor, textColor }
                                    : label
                            )
                        );
                    }
                }

                const pdfDate = format(new Date(), "MMM d, yyyy"); // formattedDateRange(dateRange);

                const filename = reportNaming({
                    dealersshipName: userAuth.dealerV2.name,
                    report: 'Knock Out Report',
                    inventory: userAuth.inventory.value,
                    extension: 'xlsx'
                });

                generateXLS(
                    {
                        head: groupedHeadFormatted,
                        body: bodyFormatted,
                        formattedDate: pdfDate,
                        title: "Knock Out Report",
                    },
                    filename
                );
                setIsLoading(false);
            } catch (err) {
                console.log("err", err);
                toast.error("Can't export XLS file. Please contact your admin");
            }
        },
        [fetchParameters, providers, userAuth.inventory.value, columnVisibility]
    );

    const onGenerateXLSX: TActionProps<TGenerateXLSFunction> = useCallback(
        async (generateXLS, setIsLoading, provider) => {
            setIsLoading(true);
            
            try {
                // let hasNextPage = true;
                let collectedResults: TTipReportV2Results[] = [];
                // let mapOffset = defaultOffset;

                let hasNextPage = true;

                let tempPage = 1;

                do {
                    const { next, results } =
                        await getSalesAsync<EModules.TipReportV2>({
                            ...fetchParameters,
                            params: {
                                ...fetchParameters.params,
                                page_size: 1000,
                                page: tempPage,
                                // offset: mapOffset,
                            },
                        })();

                    collectedResults = [...collectedResults, ...results];

                    tempPage++;
                    // console.log('next', next);

                    hasNextPage = next ? true : false;
                    // mapOffset = mapOffset + defaultPageSize;
                } while (hasNextPage);

                let groupedHeadFormatted: [IHeadObjectXLS[], (string | IHeadObjectXLS)[]] = [[], []];

                // filter providers
                const filteredProviders = providers.filter(({ value }) => value);

                // filter columns
                const filteredXlsColumns = getXLSColumns(userAuth.inventory.value, columnVisibility, knockout).map((column) => {
                        if (column.columns) {
                            column.columns = column.columns.filter(
                                (groupedColumn) =>
                                    !groupedColumn.filterColumnId ||
                                    filteredProviders.some(
                                        (provider) => provider.id === groupedColumn.filterColumnId
                                    )
                            );
                        }
                        return column;
                    })

                    .filter(
                        (column) =>
                            !column.filterColumnId ||
                            filteredProviders.some(
                                (provider) => provider.id === column.filterColumnId
                            )
                    );

                // rest of the code remains the same
                const bodyFormatted = collectedResults.map((values) => {
                    const groupedColumns = filteredXlsColumns
                        // .filter(({ filterColumnId: columnFilterId }) => columnFilterId === filterColumnId)
                        .map(({ columns: groupColumns, func, key, filterColumnId }) => {
                            if (!groupColumns) {
                                
                              const tempValue = (func?.(values[key], values) as string) || (values[key] as string)
                              
                              return tempValue === 'nan' ? '' : tempValue;
                              // return (
                              //       (func?.(values[key], values) as string) ||
                              //       (values[key] as string)
                              //   );
                            } else {
                                return groupColumns.map(({ func, key }) => {
                                    // console.log('func', values);

                                    const tempValue = (func?.(values[key], values) as string) || (values[key] as string)
                                    
                                    return tempValue === 'nan' ? '' : tempValue;
                                    // return (
                                    //     (func?.(values[key], values) as string) ||
                                    //     (values[key] as string)
                                    // );
                                });
                            }
                        });

                    return groupedColumns.flat();
                });

                for (
                    let indexHeadColumn = 0;
                    indexHeadColumn < filteredXlsColumns.length;
                    indexHeadColumn++
                ) {
                    const {
                        colspan,
                        label,
                        bgColor,
                        textColor,
                        columns: groupedColumns,
                    } = filteredXlsColumns[indexHeadColumn];

                    console.log('bgColor', bgColor);

                    groupedHeadFormatted[0].push({ colspan, label, bgColor, textColor });

                    if (groupedColumns && groupedColumns.length) {
                        groupedColumns.map(({ label, bgColor: colBgColor, textColor }) =>
                            groupedHeadFormatted[1].push(
                                colBgColor || textColor
                                    ? { label, bgColor: colBgColor, textColor }
                                    : label
                            )
                        );
                    }
                }

                const pdfDate = format(new Date(), "MMM d, yyyy"); // formattedDateRange(dateRange);

                const filename = reportNaming({
                    dealersshipName: userAuth.dealerV2.name,
                    report: 'Tip Report',
                    inventory: userAuth.inventory.value,
                    extension: 'xlsx'
                });

                generateXLS(
                    {
                        head: groupedHeadFormatted,
                        body: bodyFormatted,
                        formattedDate: pdfDate,
                        title: "Tip Report",
                    },
                    filename
                );
                setIsLoading(false);
            } catch (err) {
                console.log("err", err);
                toast.error("Can't export XLS file. Please contact your admin");
            }
        },
        [fetchParameters, providers, userAuth.inventory.value, columnVisibility, knockout]
    );

    const onGeneratePDF: TActionProps<TGeneratePdfFunction> = useCallback(
        async (onGeneratePDF, setIsLoading, provider) => {
            setIsLoading(true);

            console.log("provider", provider);

            try {
                // let hasNextPage = true;
                let collectedResults: TTipReportV2Results[] = [];
                // let mapOffset = defaultOffset;

                let hasNextPage = true;

                let tempPage = 1;

                do {
                    const { next, results } =
                        await getSalesAsync<EModules.TipReportV2>({
                            ...fetchParameters,
                            params: {
                                ...fetchParameters.params,
                                page: tempPage,
                                ...(provider && { source: [provider.id] }),
                                // offset: mapOffset,
                            },
                        })();

                    collectedResults = [...collectedResults, ...results];

                    tempPage++;
                    // console.log('next', next);

                    hasNextPage = next ? true : false;
                    // mapOffset = mapOffset + defaultPageSize;
                } while (hasNextPage);

                const filteredPDFColumns = getPdfColumns(userAuth.inventory.value, columnVisibility, knockout).filter(
                    ({ filterColumnId }) =>
                        !filterColumnId || filterColumnId === provider?.id
                );

                const bodyFormatted = collectedResults.map((values) => {
                    return filteredPDFColumns.map(
                        ({ func, key }) =>
                            func?.(values[key], values) || (values[key] as string)
                    );
                });

                // const dateRangeTitle = formattedDateRange(dateRange);

                const pdfDate = format(new Date(), "MMM d, yyyy"); // formattedDateRange(dateRange);

                const filename = reportNaming({
                    dealersshipName: userAuth.dealerV2.name,
                    report: 'Tip Report',
                    provider: provider ? provider.label : '',
                    inventory: userAuth.inventory.value,
                    extension: 'pdf'
                });
                
                onGeneratePDF(
                    {
                        head: [filteredPDFColumns.map(({ label }) => label)],
                        body: bodyFormatted,
                        ...onGeneratePDFDefaultType(
                            provider
                                ? `Tip Report - ${provider.label}`
                                : `Tip Report`,
                                pdfDate
                        ),
                    },
                    filename,
                    {
                        orientation: "landscape",
                        compress: true,
                    }
                );
                setIsLoading(false);
            } catch (err) {
                toast.error("Can't export PDF file. Please contact your admin");
            }
        },
        [userAuth.inventory.value, userAuth.dealerV2.name, columnVisibility, fetchParameters, knockout]
    );

    const onProviderChange = (selectedProvider: IProviderCheckList) => {
        setSourceInteracted(true);
        const newSource = providers
            .map((p) => p.id === selectedProvider.id ? { ...p, value: !p.value } : p)
            .filter(({ value }) => value)
            .map(({ id }) => id);
        setQuery((prev) => ({ ...prev, source: newSource, sortBy: '' }));
    };

    // const onDateChange = (newValue: [Date, Date]) => {
    //     // setDateRange({ startDate, endDate });
    //     setDateRange({
    //         startDate: set(newValue[0], { hours: 0, minutes: 0, seconds: 0 }),
    //         endDate: set(newValue[1], { hours: 23, minutes: 59, seconds: 59 }),
    //     });
    // };

    const handleRangeChange = (range: number[]) => {
        setQuery((prev) => ({
            ...prev,
            min_price_change: range[0].toString(),
            max_price_change: range[1].toString(),
        }));
    };

    const onPageChange = useCallback((pagination: PaginationState) => {
        setQuery((prev) => ({ ...prev, page: pagination.pageIndex, page_size: pagination.pageSize }));
    }, [setQuery]);

    const onSearch = useCallback((search: string) => {
        setQuickSearch(search);
        setQuery((prev) => ({ ...prev, search, page: 0 }));
    }, [setQuery]);

    const onSavePreferences = () => {
        
        // Find and update or add the current dealership's price change
        const updatedPriceChanges = [
            // Keep all other dealerships unchanged
            ...defaultFilters.price_changes.filter(
                pc => pc.dealership_uid !== userAuth.dealerV2.uid
            ),
            // Add or update the current dealership
            {
                dealership_uid: userAuth.dealerV2.uid,
                min_price_change: effectiveMinPrice,
                max_price_change: effectiveMaxPrice,
            }
        ];

        const pagePrefrence = setDefaultFiltersV2<TSavedFilters>(ELocalSavedFilters.TipReportTableV2, {
            dateRange: { startDate: parseISO(query.startDate), endDate: parseISO(query.endDate) },
            pageSize: query.page_size,
            providers,
            columnVisibility,
            columnSizing,
            isDefaultTipReportConfigSet: true,
            price_changes: updatedPriceChanges
        });

        console.log(JSON.parse(pagePrefrence));

        savePagePreference({
            settings: JSON.parse(pagePrefrence),
        }, {
            onError: () => {
                toast.error("Failed to save preferences");
            }, 
            onSuccess: () => {
                toast.success("Page preferences saved successfully");
            }
        });
    };

    const onKnockoutToggle = () => {
        setKnockout((prev) => {
            const newKnockout = !prev;
            setQuery((prevQuery) => ({ ...prevQuery, knockout: newKnockout }));
            return newKnockout;
        });
    }

    const columnSidebarChilrend = () => {
        return (
            <Box display="flex" flexFlow="column" gap="10px">
                <Box display="flex" flexFlow="column" gap="10px">
                    <Text fontWeight="600">Providers</Text>
                    <CustomizeProvider 
                        providers={providers} 
                        onChange={onProviderChange} 
                    />
                </Box>
                <Box display="flex" flexFlow="column" gap="10px">
                    <Text fontWeight="600">Report Type</Text>
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        gap="10px"
                        padding="10px 12px"
                        border="1px solid #b3b8db"
                        borderRadius="6px"
                        backgroundColor="#ffffff"
                    >
                        <Text fontSize="14px" fontWeight="500">Knockout</Text>
                        <Switch
                            isChecked={knockout}
                            onChange={onKnockoutToggle}
                            colorScheme="green"
                            size="lg"
                        />
                    </Box>
                </Box>
            </Box>
        );
    };

    const conttroller = () => {
        return (
            <Box
                display="flex"
                gap="15px"
                justifyContent="space-between"
                flex="1"
                alignItems="center"
            >
                <Box display="flex" alignItems="center" gap="15px" flex="1">
                    <RangePopover
                        defaultValue={[effectiveMinPrice, effectiveMaxPrice]}
                        header="Price Change"
                        onChange={handleRangeChange}
                        max={effectiveMaxRange}
                        onMaxChange={(value) => setQuery((prev) => ({ ...prev, max_range: value.toString() }))}
                        onReset={() => {
                            setQuery((prev) => ({
                                ...prev,
                                min_price_change: '0',
                                max_price_change: '500',
                                max_range: '500',
                            }));
                        }}
                    >
                        <Button 
                            leftIcon={<Icon as={FaMoneyBillTrendUp} color="#8bb9a8" fontSize="large" />}
                            size="md"
                            border="1px solid #b3b8db"
                            backgroundColor="#ffffff"
                            fontSize="14px"
                            padding="10px 16px"
                            // height="43px"
                        >
                            Price Change
                        </Button>
                  </RangePopover>
                </Box>
                <Box>
                    <ActionButtonV2
                        actionConfig={{
                            isDownloadXLS: true,
                            isProviderPrint: true,
                            isPrintPDF: true,
                            isEmailPDF: true,
                            isEmailXLS: true,
                            isDownloadPDF: true,
                        }}
                        providers={providers}
                        onDownloadPDF={onGeneratePDF}
                        onDownloadXLS={onGenerateXLSX}
                        onGeneratePDF={onGeneratePDF}
                        customDownloadMenu={[{
                            label: 'Knock Out',
                            onDownload: onDonwloadKnockOut,
                        }]}
                        downloadAllProvider={{
                            label: 'All Providers (Tip Report)',
                            show: !knockout, // Show this option only when knockout is false
                        }}
                    />
                </Box>
            </Box>
        );
    };

    return (
        <>
            <Box display="flex" flexDirection="column" gap="15px" flex={1}>
                {/* <DateRange
                    onChange={onDateChange}
                    quickOptions={commonDateRanges}
                    value={dateRange}
                /> */}
                <DataTable<TTipReportV2Results>
                    data={data?.results || []}
                    rowCount={data?.count || 0}
                    onPageChange={onPageChange}
                    sort={sortBy}
                    onSort={onSort}
                    columns={filteredColumns as any}
                    pageIndex={query.page}
                    pageSize={query.page_size}
                    isLoading={isFetching}
                    conttroller={conttroller()}
                    onSearch={onSearch}
                    search={quickSearch}
                    pageCount={data?.total_pages}
                    onColumnVisibilityChange={setColumnVisibility}
                    columnVisibility={columnVisibility}
                    onColumnSizingChange={setColumnSizing}
                    columnSizing={columnSizing}
                    showColumns={true}
                    // onUpdateData={updateData}
                    columnsSidebarChildren={columnSidebarChilrend()}
                    triggerButtonType="icon"
                    onSavePreferences={onSavePreferences}
                    disableAllDrag={true}
                />
            </Box>
        </>
    );
};
export default TipReportV2;
